import {
  EMPTY_MINTROUTE_DETAILS,
  type MapProvidersValues,
  type MappingFormValues,
} from '../schemas/mapping.schema';
import type { Mapping, MappingPayload } from '../types/provider.types';

type RowField =
  | 'vendorId'
  | 'mintroute.category'
  | 'cost'
  | 'productIdentifier'
  | 'mintroute.denominationId'
  | 'mintroute.brandId'
  | 'mintroute.wallet'
  | 'mintroute.vat';

const ROW_FIELDS: Record<string, RowField> = {
  vendorid: 'vendorId',
  'mintroute.category': 'mintroute.category',
  cost: 'cost',
  productidentifier: 'productIdentifier',
  'mintroute.denominationid': 'mintroute.denominationId',
  'mintroute.brandid': 'mintroute.brandId',
  'mintroute.wallet': 'mintroute.wallet',
  'mintroute.vat': 'mintroute.vat',
};

type RowPath = `items.${number}.${RowField}`;

/**
 * An Admin API field error key → the form path it belongs to.
 *   batch create: `Items[1].Mintroute.BrandId` → `items.1.mintroute.brandId`
 *   single edit:  `Mintroute.BrandId`          → `items.0.mintroute.brandId`
 * Null for a key that is not a row field.
 */
export function toFormPath(field: string): RowPath | null {
  const match = /^(?:Items\[(\d+)\]\.)?(.+)$/i.exec(field);
  const name = ROW_FIELDS[(match?.[2] ?? '').toLowerCase()];
  if (!match || !name) return null;
  return `items.${Number(match[1] ?? 0)}.${name}`;
}

/** Form row → the cost, product id and (Mintroute only) details the API takes. */
export function toMappingPayload(item: MappingFormValues): MappingPayload {
  const trimmed = item.productIdentifier.trim();
  return {
    cost: item.cost,
    productIdentifier: trimmed === '' ? null : trimmed,
    mintroute:
      item.integration === 'Mintroute'
        ? {
            category: item.mintroute.category.trim(),
            brandId: item.mintroute.brandId,
            denominationId: item.mintroute.denominationId,
            wallet: item.mintroute.wallet.trim().toUpperCase(),
            vat: item.mintroute.vat,
          }
        : null,
  };
}

/** An existing mapping as the edit form's single row; Mintroute fields pre-filled from its stored details. */
export function toFormValues(mapping: Mapping): MapProvidersValues {
  const stored = mapping.mintroute;
  return {
    items: [
      {
        vendorId: mapping.vendorId,
        cost: mapping.cost ?? Number.NaN,
        productIdentifier: mapping.productIdentifier ?? '',
        isActive: mapping.isActive,
        identifierRequired: mapping.productIdentifierRequired,
        integration: mapping.integration,
        mintroute: {
          // The stored text; our brand's category when there is none yet.
          category: stored?.category ?? mapping.categoryName ?? '',
          denominationId: stored?.denominationId ?? EMPTY_MINTROUTE_DETAILS.denominationId,
          brandId: stored?.brandId ?? EMPTY_MINTROUTE_DETAILS.brandId,
          wallet: stored?.wallet || EMPTY_MINTROUTE_DETAILS.wallet,
          vat: stored?.vat === 'YES' ? 'YES' : 'NO',
        },
      },
    ],
  };
}
